/* eslint-disable jsx-a11y/anchor-is-valid */
import { Combobox } from "@/components/ui/combobox";
import { inputClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import useActsLookup from "hooks/queries/useActsLookup";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createActsPriceInsurer,
  deleteActsPriceInsurer,
  getActsPriceInsurerByInsurer,
  updateActsPriceInsurer,
} from "../../redux/acts/actions";
import { getStorageInsurer } from "../../utils/storageUtils";
import DashboardWrapper from "../Layout/DashboardWrapper";
import ModalUI from "../legacy/Modal";

const storageInsurer = getStorageInsurer();

// Formatage FCFA aligné sur la convention locale fr-FR (espace fine comme
// séparateur de milliers). Évite le `${value}f.cfa` legacy peu lisible.
const formatFcfa = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${new Intl.NumberFormat("fr-FR").format(n)} FCFA`;
};

const initialsOf = (name) => {
  if (!name) return null;
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || null;
};

// Form vide pour l'ajout — sert aussi de reset après un succès.
const EMPTY_FORM = { actsId: null, price: "" };

const AssureurDetails = () => {
  const dispatch = useDispatch();

  // --- State serveur (redux) — slice acts ---------------------------------
  const acts_price_insurer = useSelector(
    (s) => s.actsReducer.acts_price_insurer
  );
  const getting_acts_price_insurer = useSelector(
    (s) => s.actsReducer.getting_acts_price_insurer
  );
  const creating_acts_price_insurer = useSelector(
    (s) => s.actsReducer.creating_acts_price_insurer
  );
  const updating_acts_price_insurer = useSelector(
    (s) => s.actsReducer.updating_acts_price_insurer
  );
  const deleting_acts_price_insurer = useSelector(
    (s) => s.actsReducer.deleting_acts_price_insurer
  );
  const errors = useSelector((s) => s.actsReducer.errors) || [];

  // current_insurer côté usersReducer ; fallback storage si on arrive
  // directement sur la route sans avoir cliqué une ligne.
  const current_insurer_redux = useSelector(
    (s) => s.usersReducer.current_insurer
  );
  const current_insurer =
    current_insurer_redux && Object.keys(current_insurer_redux).length
      ? current_insurer_redux
      : storageInsurer;

  // --- State local --------------------------------------------------------
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");

  // editingRow === null → mode liste seule ; sinon → édition d'une ligne.
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Acts catalogue (lookup léger — pas l'endpoint paginé).
  const { data: acts = [] } = useActsLookup();

  // --- Fetch initial ------------------------------------------------------

  // Recharge la liste à chaque changement d'assureur courant (l'ancien
  // useEffect avait `[]` en deps et ratait un éventuel switch sans
  // démontage du composant).
  useEffect(() => {
    if (current_insurer?.id != null) {
      dispatch(getActsPriceInsurerByInsurer(current_insurer.id));
    }
  }, [dispatch, current_insurer?.id]);

  // --- Auto-reset après succès (saga toast déjà géré côté redux) ----------

  // Reset le form de création quand creating_acts_price_insurer passe
  // true → false ET qu'aucune erreur n'a été remontée → c'est un succès.
  // Permet de re-saisir un autre tarif tout de suite sans cliquer pour
  // vider chaque champ.
  const prevCreatingRef = useRef(creating_acts_price_insurer);
  useEffect(() => {
    if (prevCreatingRef.current && !creating_acts_price_insurer) {
      if (errors.length === 0) {
        setForm(EMPTY_FORM);
      }
    }
    prevCreatingRef.current = creating_acts_price_insurer;
  }, [creating_acts_price_insurer, errors]);

  // Idem pour l'édition : ferme le modal au succès.
  const prevUpdatingRef = useRef(updating_acts_price_insurer);
  useEffect(() => {
    if (prevUpdatingRef.current && !updating_acts_price_insurer) {
      if (errors.length === 0) {
        setEditingRow(null);
      }
    }
    prevUpdatingRef.current = updating_acts_price_insurer;
  }, [updating_acts_price_insurer, errors]);

  // Idem pour la suppression : ferme le modal de confirmation au succès.
  const prevDeletingRef = useRef(deleting_acts_price_insurer);
  useEffect(() => {
    if (prevDeletingRef.current && !deleting_acts_price_insurer) {
      if (errors.length === 0) {
        setDeleteCandidate(null);
      }
    }
    prevDeletingRef.current = deleting_acts_price_insurer;
  }, [deleting_acts_price_insurer, errors]);

  // --- Handlers ------------------------------------------------------------

  const handleCreate = (e) => {
    e.preventDefault();
    if (creating_acts_price_insurer) return;
    if (form.actsId == null || form.price === "") return;
    dispatch(
      createActsPriceInsurer({
        actsId: form.actsId,
        price: Number(form.price),
        insurerId: current_insurer?.id,
      })
    );
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({
      // Le back renvoie `actsId` à plat (pas un objet `acts` niché).
      actsId: row?.actsId ?? null,
      price: row?.price ?? "",
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (updating_acts_price_insurer) return;
    if (!editingRow || editForm.actsId == null || editForm.price === "") return;
    dispatch(
      updateActsPriceInsurer({
        id: editingRow.id,
        actsId: editForm.actsId,
        price: Number(editForm.price),
        insurerId: current_insurer?.id,
      })
    );
  };

  const handleConfirmDelete = () => {
    if (deleting_acts_price_insurer || deleteCandidate?.id == null) return;
    dispatch(deleteActsPriceInsurer(deleteCandidate));
  };

  // --- Données dérivées ---------------------------------------------------

  const actOptions = useMemo(
    () => (acts || []).map((a) => ({ value: a?.id, label: a?.label })),
    [acts]
  );

  // Recherche client-side (endpoint /by-insurer non paginé).
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = acts_price_insurer || [];
    if (!q) return rows;
    return rows.filter((r) => (r?.actsLabel || "").toLowerCase().includes(q));
  }, [acts_price_insurer, search]);

  const totalConfigured = (acts_price_insurer || []).length;
  const insurerInitials = initialsOf(current_insurer?.name);
  const editingActLabel =
    editingRow?.actsLabel ||
    actOptions.find((o) => o.value === editForm.actsId)?.label ||
    "—";
  const deletingActLabel = deleteCandidate?.actsLabel || "—";

  return (
    <DashboardWrapper
      showSpinner={
        getting_acts_price_insurer ||
        creating_acts_price_insurer ||
        updating_acts_price_insurer ||
        deleting_acts_price_insurer
      }
    >
      <>
        {/* Confirmation suppression — contextuelle, ton destructive */}
        <ModalUI
          isOpen={deleteCandidate != null}
          onClose={() => {
            if (!deleting_acts_price_insurer) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <i className="bx bx-trash text-2xl" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                  Action irréversible
                </p>
                <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                  Supprimer ce tarif ?
                </h2>
                <p className="text-sm text-slate-500">
                  Le tarif négocié pour{" "}
                  <span className="font-medium text-slate-800">
                    {deletingActLabel}
                  </span>
                  {deleteCandidate?.price != null && (
                    <>
                      {" "}
                      (<span className="font-medium text-slate-800">
                        {formatFcfa(deleteCandidate.price)}
                      </span>)
                    </>
                  )}{" "}
                  sera supprimé pour{" "}
                  <span className="font-medium text-slate-800">
                    {current_insurer?.name}
                  </span>
                  . Cette action ne peut pas être annulée.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={deleting_acts_price_insurer}
                className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting_acts_price_insurer}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {deleting_acts_price_insurer ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Suppression…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash text-base" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalUI>

        {/* Modal édition — eyebrow + nom de l'acte en titre, structure 3 zones */}
        <ModalUI
          isOpen={editingRow != null}
          onClose={() => {
            if (!updating_acts_price_insurer) setEditingRow(null);
          }}
          styles={{ width: "min(560px, 92vw)", padding: 0 }}
        >
          <form
            className="flex flex-col"
            onSubmit={handleUpdate}
          >
            {/* Header */}
            <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Modifier le tarif
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-800 truncate">
                  {editingActLabel}
                </h2>
                {current_insurer?.name && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    pour {current_insurer.name}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!updating_acts_price_insurer) setEditingRow(null);
                }}
                aria-label="Fermer"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <i className="bx bx-x text-xl" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 sm:px-8 py-6 space-y-5">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Acte
                </span>
                <Combobox
                  options={actOptions}
                  value={editForm.actsId ?? null}
                  fallbackLabel={editingRow?.actsLabel}
                  placeholder="Sélectionner un acte"
                  searchPlaceholder="Rechercher un acte..."
                  className="w-full mt-1"
                  onChange={(val) =>
                    setEditForm((f) => ({ ...f, actsId: val?.value ?? null }))
                  }
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Prix négocié (FCFA)
                </span>
                <div className="relative">
                  <i className="bx bx-money absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                  <input
                    type="number"
                    name="price"
                    min={0}
                    value={editForm.price}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, price: e.target.value }))
                    }
                    placeholder="0"
                    className={cn(inputClass, "pl-11")}
                  />
                </div>
              </label>
            </div>

            {/* Footer */}
            <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!updating_acts_price_insurer) setEditingRow(null);
                }}
                disabled={updating_acts_price_insurer}
                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={
                  updating_acts_price_insurer ||
                  editForm.actsId == null ||
                  editForm.price === ""
                }
                className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                {updating_acts_price_insurer ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Enregistrement…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-save text-lg" />
                    <span>Enregistrer</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </ModalUI>

        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          {/* Hero contextualisé avec avatar de l'assureur */}
          <header className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white text-base font-semibold ring-4 ring-white shadow-lg">
                  {insurerInitials || <i className="bx bx-shield-alt-2 text-xl" />}
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                    Catalogue tarifaire
                  </p>
                  <h1 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-slate-900 truncate">
                    {current_insurer?.name || "Assureur"}
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    {totalConfigured} acte{totalConfigured > 1 ? "s" : ""} tarifé
                    {totalConfigured > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              {current_insurer?.address && (
                <span className="inline-flex items-center gap-1.5 self-start sm:self-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                  <i className="bx bx-map-pin text-sm text-emerald-600" />
                  {current_insurer.address}
                </span>
              )}
            </div>
          </header>

          {/* Carte : ajouter un tarif */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Nouveau tarif
                </p>
                <h2 className="mt-0.5 text-sm font-semibold text-slate-800">
                  Configurer un acte
                </h2>
              </div>
            </header>
            <form
              onSubmit={handleCreate}
              className="px-5 py-5 grid grid-cols-1 gap-4 sm:grid-cols-[1fr_220px_auto] sm:items-end"
            >
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Acte
                </span>
                <Combobox
                  options={actOptions}
                  value={form.actsId ?? null}
                  placeholder="Sélectionner un acte"
                  searchPlaceholder="Rechercher un acte..."
                  className="w-full mt-1"
                  onChange={(val) =>
                    setForm((f) => ({ ...f, actsId: val?.value ?? null }))
                  }
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Prix négocié (FCFA)
                </span>
                <div className="relative">
                  <i className="bx bx-money absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                  <input
                    type="number"
                    name="price"
                    min={0}
                    value={form.price}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, price: e.target.value }))
                    }
                    placeholder="0"
                    className={cn(inputClass, "pl-11")}
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={
                  creating_acts_price_insurer ||
                  form.actsId == null ||
                  form.price === "" ||
                  current_insurer?.id == null
                }
                className="group/btn relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-medium px-5 h-11 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                />
                {creating_acts_price_insurer ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-lg" />
                    <span>Ajout…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-plus text-lg" />
                    <span>Ajouter</span>
                  </>
                )}
              </button>
            </form>
          </section>

          {/* Search bar */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
            <label className="relative block w-full md:w-96">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none">
                <i className="bx bx-search" />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer les actes tarifés…"
                className={cn(inputClass, "pl-11 mt-0")}
              />
            </label>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="px-4 py-3 text-left">Acte</th>
                    <th className="px-4 py-3 text-right">Prix négocié</th>
                    <th className="px-4 py-3 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-16 text-center">
                        <i className="bx bx-list-check text-4xl text-slate-300" />
                        <p className="mt-2 text-sm font-medium text-slate-600">
                          {search
                            ? "Aucun acte ne correspond à la recherche"
                            : "Aucun tarif configuré"}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {search
                            ? "Essayez un autre terme de recherche."
                            : "Ajoutez un premier tarif via le formulaire ci-dessus."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((item) => (
                      <tr
                        key={item?.id}
                        className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                              <i className="bx bx-clipboard text-base" />
                            </span>
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {item?.actsLabel || "—"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-slate-800 tabular-nums">
                          {formatFcfa(item?.price)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition"
                              aria-label="Modifier"
                              title="Modifier"
                            >
                              <i className="bx bx-edit-alt text-lg" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteCandidate(item)}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                              aria-label="Supprimer"
                              title="Supprimer"
                            >
                              <i className="bx bx-trash text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    </DashboardWrapper>
  );
};

export default AssureurDetails;
